/**
 * KAN-650: смена прокси (VPN) подключённого Telegram-аккаунта без повторного входа.
 * Кнопка "Прокси" в строке таблицы открывает диалог: поля прокси, "Убрать прокси",
 * предупреждение и подтверждение для сессии моложе суток.
 * KAN-713: над полями выбор "Прокси из справочника" (в запрос уходит proxy_id).
 * KAN-715: при открытии выбран текущий прокси аккаунта из справочника
 * (proxy_directory_id, иначе совпадение host:port).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ProxyDirectoryPicker } from '@/components/telegram/ProxyDirectoryPicker';
import { ProxyFields } from '@/components/telegram/ProxyFields';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTelegramProxies } from '@/hooks/useTelegramProxies';
import { describeApiError } from '@/lib/telegramConnect';
import {
  FRESH_PROXY_WARNING,
  PROXY_SAVE_ERROR,
  errorCode,
  directoryProxyBody,
  formFromHost,
  proxyBody,
  removeBody,
  savedToast,
  validateProxyForm,
  type ProxyFormValues,
} from '@/lib/telegramProxy';
import { accountDirectoryProxy } from '@/lib/telegramProxyDirectory';
import { FRESH_SESSION_CODE, isFreshSession } from '@/lib/telegramSessionAge';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { ProxyChangeBody, TelegramAccount } from '@/types/telegramAccount';

const QUERY_KEY = ['telegram-accounts'];
const CONFIRM_REQUIRED = 'Отметьте согласие: сессия моложе суток';

interface Props {
  account: TelegramAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AccountProxyDialog({ account, open, onOpenChange }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const key = `${account.operator_id}-${account.account_id}`;
  const [form, setForm] = useState<ProxyFormValues>(() => formFromHost(account.proxy_host));
  const [proxyId, setProxyId] = useState('');
  const [fresh, setFresh] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // KAN-715: после открытия один раз выбрать текущий прокси аккаунта из справочника
  const [autoPick, setAutoPick] = useState(true);
  const { data: directory } = useTelegramProxies(open);

  useEffect(() => {
    if (!open) return;
    setForm(formFromHost(account.proxy_host));
    setProxyId('');
    setAutoPick(true);
    setFresh(isFreshSession(account));
    setConfirm(false);
    setError(null);
    // открытие диалога сбрасывает форму; account меняется при каждом опросе списка
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !autoPick || !directory) return;
    const current = accountDirectoryProxy(account, directory);
    if (current) setProxyId(String(current.id));
    setAutoPick(false);
    // выбор делается по данным на момент открытия, опрос списка аккаунтов его не сбивает
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoPick, directory]);

  const save = useMutation({
    mutationFn: (body: ProxyChangeBody) =>
      telegramAccountsApi.changeProxy(account.account_id, body, account.operator_id),
    onSuccess: (result, body) => {
      toast.success(savedToast(result.status, Boolean(body.remove)));
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      onOpenChange(false);
    },
    onError: (err) => {
      if (errorCode(err) === FRESH_SESSION_CODE) setFresh(true);
      setError(describeApiError(err, PROXY_SAVE_ERROR));
    },
  });

  const submit = (body: () => ProxyChangeBody, problem: string | null): void => {
    const blocked = problem ?? (fresh && !confirm ? CONFIRM_REQUIRED : null);
    setError(blocked);
    if (!blocked) save.mutate(body());
  };

  const busy = save.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent data-testid={`proxy-dialog-${key}`}>
        <DialogHeader>
          <DialogTitle>Прокси аккаунта</DialogTitle>
          <DialogDescription>
            {`${account.label || account.account_id}: сессия та же, меняется только соединение. ` +
              `Сейчас: ${account.proxy_host || 'без прокси'}.`}
          </DialogDescription>
        </DialogHeader>
        <ProxyDirectoryPicker
          idPrefix={`proxy-${key}`}
          proxyId={proxyId}
          disabled={busy}
          onChange={setProxyId}
          manual={
            <ProxyFields
              idPrefix={`proxy-${key}`}
              values={form}
              disabled={busy}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
          }
        />
        <p className="text-xs text-gray-500">
          Перед сохранением сервер проверит, что прокси отвечает. Логин и пароль не
          показываются: при смене введите их заново.
        </p>
        {fresh && (
          <div
            className="flex flex-col gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"
            data-testid={`proxy-fresh-${key}`}
          >
            <span>{FRESH_PROXY_WARNING}</span>
            <label className="flex items-center gap-2">
              <Checkbox
                data-testid={`proxy-confirm-${key}`}
                checked={confirm}
                disabled={busy}
                onCheckedChange={setConfirm}
              />
              Всё равно сменить
            </label>
          </div>
        )}
        {error && (
          <p className="text-xs text-red-700" data-testid={`proxy-error-${key}`}>
            {error}
          </p>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="outline"
            className="text-red-700"
            disabled={busy}
            data-testid={`proxy-remove-${key}`}
            onClick={() => submit(() => removeBody(confirm), null)}
          >
            Убрать прокси
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button
              data-testid={`proxy-save-${key}`}
              disabled={busy}
              onClick={() =>
                proxyId
                  ? submit(() => directoryProxyBody(Number(proxyId), confirm), null)
                  : submit(() => proxyBody(form, confirm), validateProxyForm(form))
              }
            >
              {busy ? 'Переподключение...' : 'Сохранить'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Кнопка "Прокси" в строке аккаунта. */
export function AccountProxyButton({ account }: { account: TelegramAccount }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="mt-1.5 h-7 px-2 text-xs"
        data-testid={`proxy-${account.operator_id}-${account.account_id}`}
        onClick={() => setOpen(true)}
      >
        Прокси
      </Button>
      <AccountProxyDialog account={account} open={open} onOpenChange={setOpen} />
    </>
  );
}
